import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  Button,
  Input,
  Label,
  Alert,
  AlertDescription,
} from "@repo/ui";
import { Building2, Loader2, CheckCircle2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { DateInput } from "@/components/forms";
import { updatePharmacyDetails } from "@/lib/api/pharmacy";
import { usePharmacyStore } from "@/lib/stores/pharmacy-store";
import type { ApiClientError } from "@/lib/api/client";

interface PharmacyDetailsModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  pharmacyName: string;
  currentDetails?: {
    ncpdp_id?: string | null;
    dea_registration_number?: string | null;
    dea_expiration_date?: string | null;
    state_license_number?: string | null;
    license_expiration_date?: string | null;
  };
}

export function PharmacyDetailsModal({
  open,
  onClose,
  onSuccess,
  pharmacyName,
  currentDetails,
}: PharmacyDetailsModalProps) {
  const [ncpdp, setNcpdp] = useState(currentDetails?.ncpdp_id || "");
  const [dea, setDea] = useState(currentDetails?.dea_registration_number || "");
  const [deaExpiration, setDeaExpiration] = useState(
    currentDetails?.dea_expiration_date || ""
  );
  const [license, setLicense] = useState(
    currentDetails?.state_license_number || ""
  );
  const [licenseExpiration, setLicenseExpiration] = useState(
    currentDetails?.license_expiration_date || ""
  );

  const invalidatePharmacy = usePharmacyStore((s) => s.invalidatePharmacy);
  const fetchPharmacy = usePharmacyStore((s) => s.fetchPharmacy);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Reset state only on the false → true transition of `open`
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setNcpdp(currentDetails?.ncpdp_id || "");
      setDea(currentDetails?.dea_registration_number || "");
      setDeaExpiration(currentDetails?.dea_expiration_date || "");
      setLicense(currentDetails?.state_license_number || "");
      setLicenseExpiration(currentDetails?.license_expiration_date || "");
      setError("");
      setFieldErrors({});
      setSuccess(false);
    }
    prevOpenRef.current = open;
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const validateFields = () => {
    const errors: Record<string, string> = {};

    // NCPDP validation (7 digits, optional)
    if (ncpdp && !/^[0-9]{7}$/.test(ncpdp)) {
      errors.ncpdp = "NCPDP must be exactly 7 digits";
    }

    // DEA is required
    if (!dea) {
      errors.dea = "DEA number is required";
    } else if (!/^[A-Z]{2}[0-9]{7}$/.test(dea.toUpperCase())) {
      errors.dea = "DEA format: 2 letters followed by 7 digits";
    }

    // DEA expiration should be in the future (if provided)
    if (deaExpiration) {
      const expDate = new Date(deaExpiration);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (expDate < today) {
        errors.deaExpiration = "DEA expiration date should be in the future";
      }
    }

    // License expiration validation (if provided)
    if (licenseExpiration) {
      const expDate = new Date(licenseExpiration);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (expDate < today) {
        errors.licenseExpiration =
          "License expiration date should be in the future";
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setFieldErrors({});

    if (!validateFields()) {
      setError("Please fix the validation errors below");
      return;
    }

    setIsLoading(true);

    try {
      await updatePharmacyDetails({
        ncpdp_id: ncpdp || null,
        dea_registration_number: dea.toUpperCase() || null,
        dea_expiration_date: deaExpiration || null,
        ...(license ? { state_license_number: license } : {}),
        ...(licenseExpiration
          ? { license_expiration_date: licenseExpiration }
          : {}),
      });

      // Refresh pharmacy store so dashboard picks up new data (best-effort)
      invalidatePharmacy();
      fetchPharmacy().catch(() => {});

      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (err) {
      const message = (err as ApiClientError).message || "An error occurred";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] !p-0 !gap-0 sm:!rounded-2xl overflow-hidden max-h-[90vh]">
        <AnimatePresence mode="wait">
          {!success ? (
            <motion.div
              key="form"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="w-full"
            >
              {/* Header */}
              <div className="bg-gradient-to-br from-primary via-primary to-primary/90 p-6 text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full blur-2xl" />

                <div className="relative flex items-center gap-4">
                  <div className="flex-shrink-0">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm">
                      <Building2
                        className="h-6 w-6 text-white"
                        strokeWidth={2.5}
                      />
                    </div>
                  </div>
                  <div>
                    <DialogTitle className="text-xl font-bold text-white mb-1">
                      Complete Pharmacy Details
                    </DialogTitle>
                    <DialogDescription className="text-white/90 text-sm">
                      {pharmacyName}
                    </DialogDescription>
                  </div>
                </div>
              </div>

              {/* Form Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
                <Alert className="mb-6 border-blue-200 bg-blue-50">
                  <AlertDescription className="text-sm text-blue-900">
                    These details help activate your account and ensure
                    compliance. You can fill them out now or update them later
                    in settings.
                  </AlertDescription>
                </Alert>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    </motion.div>
                  )}

                  {/* NCPDP */}
                  <div className="space-y-2">
                    <Label htmlFor="ncpdp" className="text-sm font-medium">
                      NCPDP Number{" "}
                      <span className="text-muted-foreground font-normal">
                        (Optional)
                      </span>
                    </Label>
                    <Input
                      id="ncpdp"
                      placeholder="1234567"
                      value={ncpdp}
                      onChange={(e) => setNcpdp(e.target.value)}
                      disabled={isLoading}
                      maxLength={7}
                      className={`h-10 ${
                        fieldErrors.ncpdp
                          ? "border-destructive focus-visible:ring-destructive"
                          : ""
                      }`}
                    />
                    {fieldErrors.ncpdp ? (
                      <p className="text-xs text-destructive">
                        {fieldErrors.ncpdp}
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        7-digit pharmacy identifier
                      </p>
                    )}
                  </div>

                  {/* DEA Number and Expiration */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="dea" className="text-sm font-medium">
                        DEA Number{" "}
                        <span className="text-destructive font-normal">*</span>
                      </Label>
                      <Input
                        id="dea"
                        placeholder="AB1234567"
                        value={dea}
                        onChange={(e) => setDea(e.target.value.toUpperCase())}
                        disabled={isLoading}
                        maxLength={9}
                        className={`h-10 ${
                          fieldErrors.dea
                            ? "border-destructive focus-visible:ring-destructive"
                            : ""
                        }`}
                      />
                      {fieldErrors.dea ? (
                        <p className="text-xs text-destructive">
                          {fieldErrors.dea}
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          2 letters + 7 digits
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor="deaExpiration"
                        className="text-sm font-medium"
                      >
                        DEA Expiration{" "}
                        <span className="text-muted-foreground font-normal">
                          (Optional)
                        </span>
                      </Label>
                      <DateInput
                        id="deaExpiration"
                        value={deaExpiration}
                        onChange={(v) => setDeaExpiration(v)}
                        disabled={isLoading}
                        className={`h-10 ${
                          fieldErrors.deaExpiration
                            ? "border-destructive focus-visible:ring-destructive"
                            : ""
                        }`}
                      />
                      {fieldErrors.deaExpiration && (
                        <p className="text-xs text-destructive">
                          {fieldErrors.deaExpiration}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* License Number and Expiration */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="license" className="text-sm font-medium">
                        State License{" "}
                        <span className="text-muted-foreground font-normal">
                          (Optional)
                        </span>
                      </Label>
                      <Input
                        id="license"
                        placeholder="License number"
                        value={license}
                        onChange={(e) => setLicense(e.target.value)}
                        disabled={isLoading}
                        className={`h-10 ${
                          fieldErrors.license
                            ? "border-destructive focus-visible:ring-destructive"
                            : ""
                        }`}
                      />
                      {fieldErrors.license && (
                        <p className="text-xs text-destructive">
                          {fieldErrors.license}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor="licenseExpiration"
                        className="text-sm font-medium"
                      >
                        License Expiration{" "}
                        <span className="text-muted-foreground font-normal">
                          (Optional)
                        </span>
                      </Label>
                      <DateInput
                        id="licenseExpiration"
                        value={licenseExpiration}
                        onChange={(v) => setLicenseExpiration(v)}
                        disabled={isLoading}
                        className={`h-10 ${
                          fieldErrors.licenseExpiration
                            ? "border-destructive focus-visible:ring-destructive"
                            : ""
                        }`}
                      />
                      {fieldErrors.licenseExpiration && (
                        <p className="text-xs text-destructive">
                          {fieldErrors.licenseExpiration}
                        </p>
                      )}
                    </div>
                  </div>

                  <DialogFooter className="pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={onClose}
                      disabled={isLoading}
                    >
                      Skip for Now
                    </Button>
                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="shadow-lg shadow-primary/20"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        "Save Details"
                      )}
                    </Button>
                  </DialogFooter>
                </form>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="flex items-center justify-center p-16 bg-gradient-to-br from-green-500 via-green-600 to-green-500 relative overflow-hidden min-h-[400px]"
            >
              <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/10 rounded-full blur-3xl" />

              <div className="relative text-center space-y-4">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    delay: 0.1,
                    type: "spring",
                    stiffness: 200,
                    damping: 15,
                  }}
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm">
                    <CheckCircle2
                      className="h-10 w-10 text-white"
                      strokeWidth={2.5}
                    />
                  </div>
                </motion.div>
                <motion.h3
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-2xl font-bold text-white"
                >
                  Details Saved!
                </motion.h3>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-white/90"
                >
                  Your pharmacy details have been updated
                </motion.p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
